import { useState } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled, alpha } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';

interface SortDropdownProps {
  value: string;
  onChange: (value: string) => void;
}

interface SortOption {
  value: string;
  label: string;
}

const SORT_OPTIONS: SortOption[] = [
  { value: 'popularity', label: 'Most Popular' },
  { value: 'price_low', label: 'Price: Low to High' },
  { value: 'price_high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'newest', label: 'Newest' },
];

const SortButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  backgroundColor: theme.palette.background.paper,
  border: `1.5px solid ${theme.palette.divider}`,
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  transition: 'all 0.2s ease',
  minWidth: 200,
  justifyContent: 'space-between',

  '&:hover': {
    borderColor: alpha(theme.palette.text.primary, 0.2),
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  },

  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.08)}`,
  },

  '&:active': {
    transform: 'scale(0.98)',
  },
}));

const SortLabel = styled('span')({
  flex: 1,
  textAlign: 'left',
});

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    marginTop: 8,
    minWidth: 200,
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.primary,
  padding: '10px 16px',
  margin: '4px 8px',
  borderRadius: 8,
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,

  '&:hover': {
    backgroundColor: theme.palette.surface.hover,
  },

  '&.Mui-selected': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    fontWeight: 600,

    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
}));

const CheckIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  color: theme.palette.primary.main,
}));

export function SortDropdown({ value, onChange }: SortDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    handleClose();
  };

  const selectedOption = SORT_OPTIONS.find((option) => option.value === value) || SORT_OPTIONS[0];

  return (
    <Box>
      <SortButton
        onClick={handleClick}
        aria-controls={open ? 'sort-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <SortLabel>{selectedOption.label}</SortLabel>
        <ArrowDropDownIcon
          sx={{
            fontSize: 20,
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </SortButton>

      <StyledMenu
        id="sort-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {SORT_OPTIONS.map((option) => (
          <StyledMenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => handleSelect(option.value)}
          >
            <span>{option.label}</span>
            {option.value === value && (
              <CheckIconWrapper>
                <CheckIcon sx={{ fontSize: 18 }} />
              </CheckIconWrapper>
            )}
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </Box>
  );
}
